// Atlas Dashboard — OAuth Callback Handler
// This page is loaded when Google redirects back to the dashboard after the
// Founder approves the Gmail connection.
//
// The API's /auth/callback endpoint redirects to /auth/success?token=[JWT]
// This page captures the token from the URL, stores it, and redirects to home.

import { useEffect, useState } from "react";
import { Activity, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { setAuthToken } from "../lib/api-client";

export function AuthCallback() {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    processCallback();
  }, []);

  async function processCallback() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const error = params.get("error");

    if (error) {
      setStatus("error");
      setErrorMessage(
        params.get("message") || "Could not connect to Gmail. Please try again.",
      );
      return;
    }

    if (!token) {
      setStatus("error");
      setErrorMessage("No authentication token received. Please try connecting again.");
      return;
    }

    // Store the JWT token
    setAuthToken(token);

    // Brief success display, then redirect to home
    setStatus("success");
    setTimeout(() => {
      window.location.href = "/";
    }, 1500);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-background)",
      }}
    >
      <div
        style={{
          backgroundColor: "var(--color-card)",
          borderRadius: "12px",
          border: "1px solid var(--color-border)",
          padding: "48px",
          maxWidth: "440px",
          width: "100%",
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "24px" }}>
          <Activity size={28} color="#2563EB" />
          <span style={{ fontSize: "20px", fontWeight: 700, color: "var(--color-text-primary)" }}>
            Atlas
          </span>
        </div>

        {status === "loading" && (
          <>
            <Loader2 size={32} style={{ color: "#2563EB", marginBottom: "16px" }} className="animate-spin" />
            <p style={{ fontSize: "16px", color: "var(--color-text-secondary)" }}>
              Connecting your Gmail...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle size={32} style={{ color: "#16A34A", marginBottom: "16px" }} />
            <p style={{ fontSize: "16px", color: "var(--color-text-primary)", fontWeight: 600, marginBottom: "8px" }}>
              Gmail connected!
            </p>
            <p style={{ fontSize: "14px", color: "var(--color-text-tertiary)" }}>
              Taking you to your dashboard...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle size={32} style={{ color: "#DC2626", marginBottom: "16px" }} />
            <p style={{ fontSize: "16px", color: "var(--color-text-primary)", fontWeight: 600, marginBottom: "8px" }}>
              Connection failed
            </p>
            <p style={{ fontSize: "14px", color: "var(--color-text-secondary)", marginBottom: "24px" }}>
              {errorMessage}
            </p>
            <button
              onClick={() => {
                window.location.href = "/login";
              }}
              style={{
                padding: "10px 24px",
                backgroundColor: "#2563EB",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
